<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, reactive, ref } from 'vue'

import {
  STATUS_LABELS,
  createSku,
  deleteSku,
  listSkus,
  updateSku,
  type Sku,
  type SkuInput,
} from '../api/skus'

const rows = ref<Sku[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
const notice = ref('')

const query = reactive({ keyword: '', status: '', page: 1, size: 20 })

const formOpen = ref(false)
const editing = ref<Sku | null>(null)
const saving = ref(false)
const formError = ref<unknown>(null)
const form = reactive<SkuInput>({
  code: '',
  name: '',
  barcode: '',
  spec: '',
  unit: '件',
  weight_g: 0,
  volume_cm3: 0,
  batch_managed: false,
  shelf_life_days: 0,
  status: 'active',
})

const codeError = computed(() => (form.code.trim() ? '' : '请输入商品编码'))

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listSkus({
      page: query.page,
      size: query.size,
      keyword: query.keyword.trim() || undefined,
      status: query.status || undefined,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => void load())

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

function openCreate(): void {
  editing.value = null
  Object.assign(form, {
    code: '',
    name: '',
    barcode: '',
    spec: '',
    unit: '件',
    weight_g: 0,
    volume_cm3: 0,
    batch_managed: false,
    shelf_life_days: 0,
    status: 'active',
  })
  formError.value = null
  formOpen.value = true
}

function openEdit(row: Sku): void {
  editing.value = row
  Object.assign(form, {
    code: row.code,
    name: row.name,
    barcode: row.barcode,
    spec: row.spec,
    unit: row.unit,
    weight_g: row.weight_g,
    volume_cm3: row.volume_cm3,
    batch_managed: row.batch_managed,
    shelf_life_days: row.shelf_life_days,
    status: row.status,
  })
  formError.value = null
  formOpen.value = true
}

async function save(): Promise<void> {
  if (codeError.value) return
  saving.value = true
  formError.value = null
  const input: SkuInput = {
    code: form.code.trim(),
    name: form.name.trim(),
    barcode: form.barcode.trim(),
    spec: form.spec.trim(),
    unit: form.unit.trim(),
    weight_g: Number(form.weight_g) || 0,
    volume_cm3: Number(form.volume_cm3) || 0,
    batch_managed: form.batch_managed,
    shelf_life_days: Number(form.shelf_life_days) || 0,
    status: form.status,
  }
  try {
    if (editing.value) await updateSku(editing.value.id, input)
    else await createSku(input)
    formOpen.value = false
    await load(editing.value ? '商品已更新' : '商品已创建')
  } catch (e) {
    formError.value = e
  } finally {
    saving.value = false
  }
}

async function remove(row: Sku): Promise<void> {
  notice.value = ''
  try {
    await deleteSku(row.id)
    await load(`已删除商品「${row.code}」`)
  } catch (e) {
    error.value = e
  }
}
</script>

<template>
  <div class="yc-sku">
    <header class="yc-sku__bar">
      <el-input
        v-model="query.keyword"
        class="yc-sku__keyword"
        placeholder="编码 / 名称 / 条码"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select v-model="query.status" class="yc-sku__status" placeholder="全部状态" clearable @change="search">
        <el-option v-for="(label, value) in STATUS_LABELS" :key="value" :label="label" :value="value" />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button v-perm="'wms:sku:create'" type="primary" @click="openCreate">新建商品</el-button>
      <el-button @click="load()">刷新</el-button>
      <span v-if="notice" class="yc-sku__notice">{{ notice }}</span>
    </header>

    <div v-if="error" class="yc-sku__error">
      <TraceIdAlert :error="error" />
    </div>

    <el-card v-loading="loading" class="yc-sku__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column prop="code" label="商品编码" min-width="140" />
        <el-table-column prop="name" label="名称" min-width="180" show-overflow-tooltip />
        <el-table-column prop="barcode" label="条码" min-width="150" show-overflow-tooltip />
        <el-table-column prop="spec" label="规格" width="120" show-overflow-tooltip />
        <el-table-column prop="unit" label="单位" width="70" />
        <el-table-column label="批次管理" width="90">
          <template #default="{ row }">
            <el-tag :type="row.batch_managed ? 'warning' : 'info'" size="small">
              {{ row.batch_managed ? '启用' : '否' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="保质期(天)" width="100" align="right">
          <template #default="{ row }">{{ row.shelf_life_days || '—' }}</template>
        </el-table-column>
        <el-table-column label="状态" width="90">
          <template #default="{ row }">
            <el-tag :type="row.status === 'active' ? 'success' : 'info'" size="small">
              {{ STATUS_LABELS[row.status as Sku['status']] ?? row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="更新时间" min-width="160">
          <template #default="{ row }">{{ formatDateTime(row.updated_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="130" fixed="right">
          <template #default="{ row }">
            <el-button v-perm="'wms:sku:update'" link type="primary" @click="openEdit(row)">编辑</el-button>
            <el-popconfirm title="确认删除该商品？" width="200" @confirm="remove(row)">
              <template #reference>
                <el-button v-perm="'wms:sku:delete'" link type="danger">删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState
        v-else-if="!loading"
        title="还没有商品档案"
        description="商品是收货、上架、拣货、盘点的共同主数据，先建一个试试。"
      >
        <el-button v-perm="'wms:sku:create'" type="primary" @click="openCreate">新建商品</el-button>
      </EmptyState>
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-sku__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />

    <el-dialog
      v-if="formOpen"
      v-model="formOpen"
      :title="editing ? `编辑商品 · ${editing.code}` : '新建商品'"
      width="560px"
      :close-on-click-modal="false"
    >
      <div v-if="formError" class="yc-sku__error">
        <TraceIdAlert :error="formError" />
      </div>

      <el-form label-position="top" @submit.prevent="save">
        <div class="yc-sku__grid">
          <el-form-item label="商品编码" :error="codeError">
            <el-input v-model="form.code" maxlength="32" placeholder="如 SP0001" />
          </el-form-item>

          <el-form-item label="名称">
            <el-input v-model="form.name" maxlength="64" placeholder="如 矿泉水 550ml" />
          </el-form-item>

          <el-form-item label="条码">
            <el-input v-model="form.barcode" maxlength="64" placeholder="如 6901234567890" />
          </el-form-item>

          <el-form-item label="规格">
            <el-input v-model="form.spec" maxlength="64" placeholder="如 550ml×24" />
          </el-form-item>

          <el-form-item label="单位">
            <el-input v-model="form.unit" maxlength="16" placeholder="如 件" />
          </el-form-item>

          <el-form-item label="保质期（天）">
            <el-input-number v-model="form.shelf_life_days" :min="0" :max="3650" controls-position="right" />
          </el-form-item>

          <el-form-item label="单件重量（g）">
            <el-input-number v-model="form.weight_g" :min="0" controls-position="right" />
          </el-form-item>

          <el-form-item label="单件体积（cm³）">
            <el-input-number v-model="form.volume_cm3" :min="0" controls-position="right" />
          </el-form-item>
        </div>

        <el-form-item label="批次管理">
          <el-switch v-model="form.batch_managed" active-text="启用" inactive-text="不启用" />
          <span class="yc-sku__hint">启用后收货必须填批次号；填了生产日期会按保质期自动推算效期。</span>
        </el-form-item>

        <el-form-item label="状态">
          <el-radio-group v-model="form.status">
            <el-radio value="active">启用</el-radio>
            <el-radio value="disabled">停用</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-button type="primary" native-type="submit" :loading="saving" :disabled="!!codeError">
          {{ editing ? '保存' : '创建' }}
        </el-button>
      </el-form>

      <template #footer>
        <el-button @click="formOpen = false">取消</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.yc-sku {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 220px;
  }

  &__status {
    width: 140px;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0 var(--yc-space-4);
  }

  &__hint {
    display: block;
    margin-top: var(--yc-space-1);
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
    line-height: 1.6;
  }
}
</style>
